import {HttpStatusCode} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateChild, RouterStateSnapshot} from '@angular/router';
import {Store} from '@ngrx/store';
import {liveQuery} from 'dexie';
import {from, Observable, of} from 'rxjs';
import {map, mergeMap, take, withLatestFrom} from 'rxjs/operators';
import {DomainChosen, LoginFailure, LoginRedirect, LoginSuccess} from '../../state-management/actions/auth.actions';
import {AppState, getAuthLoggedIn, getTreeSource} from '../../state-management/reducers';
import {db} from '../components/db';
import {OfflineTreeService} from '../services/offline-tree.service';
import {UserService} from "../../auth-module/services/user.service";
import {XsrfTokenReader} from "../../auth-module/services/xsrf-token-reader.service";
import {User} from "../../auth-module/model/user";

@Injectable()
export class AuthGuard implements CanActivateChild {
  constructor(private store$: Store<AppState>, private userService: UserService,
    private xsrfTokenReader: XsrfTokenReader, private offlineTreeService: OfflineTreeService) {
  }

  private dispatchRedirect(domain: string, route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const forceLocal = route.queryParamMap.get('type') === 'local' || !!route.queryParamMap.get('user');
    console.debug('No domain, redirecting');
    this.store$.dispatch(new LoginRedirect({
      returnUrl: route.queryParamMap.get('returnUrl') || (state.url && state.url.includes(`domains/${domain}/trees`) && state.url.split('?')[0] || ''),
      domain: domain,
      local: forceLocal,
      publicUser: route.queryParams['user']
    }));
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    console.debug(`Running canActivateChild, current route: ${route.toString()}, complete state path: `, state.toString());
    const domain = route.params['domain'] && route.params['domain'].toLowerCase();

    if(!domain) {
      this.dispatchRedirect(domain, route, state);
      return of(false);
    }

    return this.store$.select(getAuthLoggedIn).pipe(withLatestFrom(this.store$.select(getTreeSource)), take(1),
      mergeMap(([isLoggedIn, treeSource]): Observable<boolean> => {
        console.debug(`Inside mergeMap, current route: ${route.toString()}, complete state: `, state.toString());
        const currentUser = this.userService.user;
        const hasXsrfCookie = !!this.xsrfTokenReader.getToken(domain);

        if (!window.navigator.onLine) {
          console.debug('Navigator thinks browser is offline');
          if (treeSource === 'offline' || !route.params['nodeid']) {
            console.debug('Tree in offline mode');
            return of(true);
          }

          return this.offlineTreeService.offlineDialog(domain).pipe(mergeMap(result => {
            if (result === 'keep-trying') {
              return this.checkLoginState(isLoggedIn, hasXsrfCookie, domain, route, currentUser, state);
            } else {
              if (result === 'offline') {
                return from(liveQuery(() => db.sites.get(domain))).pipe(map(siteDetails => {
                  this.userService.offlineLogin(siteDetails.user, siteDetails.settings);

                  this.store$.dispatch(new LoginSuccess({
                    user: siteDetails.user || currentUser,
                    siteSettings: siteDetails.settings,
                    returnUrl: '',
                    redirect: false,
                    passwordIsTemporary: false,
                    domain: domain,
                    isOnline: false
                  }));
                  return true;
                }));
              } else {
                return of(false);
              }
            }
          }));
        }

        return this.checkLoginState(isLoggedIn, hasXsrfCookie, domain, route, currentUser, state);
      }));
  }

  private checkLoginState(isLoggedIn: boolean, hasXsrfCookie: boolean, domain: string, route: ActivatedRouteSnapshot,
    currentUser: User, state: RouterStateSnapshot): Observable<boolean> {
    console.debug('Checking login state');
    if (isLoggedIn && hasXsrfCookie) {
      console.debug('is logged in already, no new login flow needed');
      return of(true);
    } else {
      if (hasXsrfCookie && !!this.userService.getSiteSettings() && this.userService.domain === domain) {
        console.debug('Login success (state needed rechecking)');
        this.store$.dispatch(new LoginSuccess({
          user: currentUser,
          siteSettings: this.userService.getSiteSettings(),
          returnUrl:  route.queryParamMap.get('returnUrl') ?? '',
          isOnline: true,
          redirect: false,
          passwordIsTemporary: !!localStorage.getItem(UserService.CHANGE_PASSWORD_NAME),
          domain: domain
        }));
        return of(true);
      }

      console.debug('Loading settings and rechecking state');
      return this.userService.checkAndUpdateLoginState(domain).pipe(map(statusCode => {
        if (statusCode === HttpStatusCode.Ok && !!this.userService.user) {
          console.debug('Login success (new login or refresh)');
          this.store$.dispatch(new LoginSuccess({
            user: this.userService.user,
            siteSettings: this.userService.getSiteSettings(),
            returnUrl: route.queryParamMap.get('returnUrl') ?? '',
            isOnline: true,
            redirect: false,
            passwordIsTemporary: false,
            domain: domain
          }));
          return true;
        } else {
          if (statusCode === HttpStatusCode.Forbidden) {
            console.log('Authorization failed: Forbidden');
            this.store$.dispatch(new LoginFailure({
              domain: domain,
              publicUser: route.queryParams['user'],
              error: new Error('login.error.partner.error.no-access'),
              errorDetails: '',
              returnUrl: route.queryParamMap.get('returnUrl')
            }));
          }
          else if (route.queryParamMap.get('type') === 'redirected') {
            const errorMessage = route.queryParamMap.get('error') || '';
            const errorDetails = route.queryParamMap.get('errorDetails') || '';
            console.log('Authorization failed: ', errorMessage, 'details: ', errorDetails);
            this.store$.dispatch(new LoginFailure({
              domain: domain,
              publicUser: route.queryParams['user'],
              error: new Error(errorMessage),
              errorDetails: errorDetails,
              returnUrl: route.queryParamMap.get('returnUrl')
            }));
          } else {
            const forceLocal = route.queryParamMap.get('type') === 'local' || !!route.queryParamMap.get('user');
            if (!forceLocal && !!domain) {
              this.store$.dispatch(new DomainChosen({
                domain: domain,
                returnUrl: state.url.split('?')[0],
                finalUrl: route.queryParamMap.get('returnUrl')
              }));
            } else {
              this.dispatchRedirect(domain, route, state);
            }
          }
          return false;
        }
      }));
    }
  }
}
